import React, { useState, useMemo } from 'react'
import { Link } from 'react-router'
import { useNavigate } from 'react-router'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { getAppBasePath } from '@klear/runtime-env'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_DELETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ENTITY_TRADER,
  ROUTE_ADMIN,
  ROUTE_TRADER
} from '@klear/constants'
import { KlearDate } from '@klear/date'
import { parseTimestamp } from '@klear/time/boundary'

import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams, GridReadyEvent, GridApi } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import ViewIcon from '@/icons/ViewIcon'
import EditIcon from '@/icons/EditIcon'
import DeleteIcon from '@/icons/DeleteIcon'
import AddIcon from '@/icons/AddIcon'
import ExportIcon from '@/icons/ExportIcon'
import { Button } from '@klear/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { MoreHorizontal } from 'lucide-react'
import { Trader } from '@klear/trader'
import { ProgressStatus, calculateProgressScore, type ProgressStage } from '@klear/ui/components/progress-status'
import type { ValueGetterParams } from 'ag-grid-community'
import { useTradersWithActivity } from '@klear/ui/hooks/use-traders'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltip } from '@klear/ui/components/chart'
import type { ChartConfig } from '@klear/ui/components/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const Traders = () => {
  useDocumentTitle('Traders')
  const { user } = useAuth()
  const navigate = useNavigate()
  const [gridApi, setGridApi] = useState<GridApi<Trader> | null>(null)

  const { traders, isLoading, error } = useTradersWithActivity()
  const agGrid = useKlearAgGrid()

  const tradersByMonth = useMemo(() => {
    if (!traders) return []
    const buckets = new Map<string, { label: string; sort: number; count: number }>()
    for (const trader of traders) {
      const createdAt = parseTimestamp(trader.createdAt)
      if (!createdAt) continue
      const key = createdAt.toString('YYYY-MM')
      const label = createdAt.toString('MMM YY')
      const entry = buckets.get(key) ?? { label, sort: parseInt(createdAt.toString('YYYYMM'), 10), count: 0 }
      buckets.set(key, { ...entry, count: entry.count + 1 })
    }
    const sorted = [...buckets.values()].sort((a, b) => a.sort - b.sort)
    let running = 0
    return sorted.map(({ label, count }) => {
      running += count
      return { label, count: running }
    })
  }, [traders])

  const traderChartConfig: ChartConfig = { count: { label: 'Traders', color: '#2563eb' } }

  const ActionCellRenderer = (props: ICellRendererParams<Trader>) => {
    const trader = props.data
    if (!trader) return null

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/${ROUTE_TRADER}/${trader.id}`} className="flex items-center gap-2">
              <ViewIcon />
              View
            </Link>
          </DropdownMenuItem>
          {user.hasPermission(PERMISSION_ENTITY_TRADER, PERMISSION_ACTION_EDIT) && (
            <DropdownMenuItem asChild>
              <Link to={`/${ROUTE_TRADER}/${trader.id}?edit=1`} className="flex items-center gap-2">
                <EditIcon />
                Edit
              </Link>
            </DropdownMenuItem>
          )}
          {user.hasPermission(PERMISSION_ENTITY_TRADER, PERMISSION_ACTION_DELETE) && (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault()
                navigate(`/${ROUTE_TRADER}/${trader.id}?delete=1`)
              }}
              className="text-red-600"
            >
              <DeleteIcon />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const ComputeTraderActivationStages = (trader: Trader): ProgressStage[] => {
    const completedKyc = trader.completedKyc ?? false
    const hasKlearFinancialAccount = trader.klearFinancialAccountAdded ?? false
    const hasVerifiedExternalBankAccount = trader.hasVerifiedExternalBankAccount ?? false
    const hasUploadedCustomers = trader.hasUploadedCustomers ?? false
    const hasUploadedSuppliers = trader.hasUploadedSuppliers ?? false
    const hasAddPO = trader.hasAddPO ?? false
    const hasFundedPO = trader.hasFundedPO ?? false
    const hasOperationEvents = trader.hasOperationEvents ?? false
    const hasPaymentEvents = trader.hasPaymentEvents ?? false

    return [
      {
        title: 'Financial accounts',
        steps: [
          {
            title: 'KYC completed',
            complete: completedKyc
          },
          {
            title: 'Klear account created',
            complete: hasKlearFinancialAccount
          },
          {
            title: 'Verify External Bank Account',
            complete: hasVerifiedExternalBankAccount
          }
        ]
      },
      {
        title: 'Upload Operational Data',
        steps: [
          {
            title: 'Customers',
            complete: hasUploadedCustomers
          },
          {
            title: 'Suppliers',
            complete: hasUploadedSuppliers
          }
        ]
      },
      {
        title: 'Purchase Orders',
        steps: [
          {
            title: 'PO added',
            complete: hasAddPO
          },
          {
            title: 'PO funded',
            complete: hasFundedPO
          }
        ]
      },
      {
        title: 'Payables',
        steps: [
          {
            title: 'Operations',
            complete: hasOperationEvents
          },
          {
            title: 'Payments',
            complete: hasPaymentEvents
          }
        ]
      }
    ]
  }

  const StatusCellRenderer = (props: ICellRendererParams<Trader>) => {
    const trader = props.data
    if (!trader) {
      return <span className="text-muted-foreground">-</span>
    }

    const stages = ComputeTraderActivationStages(trader)

    return (
      <div className="flex items-center h-full">
        <ProgressStatus stages={stages} size="small" />
      </div>
    )
  }

  const columnDefs = useMemo<ColDef<Trader>[]>(
    () => [
      {
        headerName: 'Trader ID',
        field: 'id',
        sortable: true,
        filter: 'agNumberColumnFilter',
        pinned: 'left',
        lockPosition: true,
        flex: 0,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        cellDataType: 'number',
        colId: 'id'
      },
      {
        headerName: 'Organization ID',
        field: 'organizationId',
        sortable: true,
        filter: 'agNumberColumnFilter',
        pinned: 'left',
        lockPosition: true,
        flex: 0,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        cellDataType: 'number',
        colId: 'organizationId'
      },
      {
        headerName: 'Name',
        field: 'name',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 200,
        cellDataType: 'text',
        colId: 'name',
        cellRenderer: (params: ICellRendererParams<Trader>) => {
          const trader = params.data
          const name = trader?.name ?? params.value ?? '—'
          if (!trader?.id) return <span>{name}</span>
          return (
            <a
              href={`${getAppBasePath(ROUTE_ADMIN)}/${ROUTE_TRADER}/${trader.id}`}
              className="text-foreground hover:text-foreground underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </a>
          )
        }
      },
      {
        headerName: 'Created Date',
        field: 'createdAt',
        type: 'klearDate'
      },
      {
        headerName: 'Klear Balance',
        field: 'klearAccountBalance',
        cellDataType: 'money',
        minWidth: 130,
        colId: 'klearBalance'
      },
      {
        headerName: 'Activation',
        colId: 'activation',
        sortable: true,
        filter: false,
        cellRenderer: StatusCellRenderer,
        valueGetter: (params: ValueGetterParams<Trader>) => {
          const trader = params.data
          if (!trader) return 0
          const stages = ComputeTraderActivationStages(trader)
          return calculateProgressScore(stages)
        },
        cellDataType: 'number',
        minWidth: 300
      },
      {
        headerName: '',
        colId: 'actions',
        cellRenderer: ActionCellRenderer,
        sortable: false,
        filter: false,
        pinned: 'right',
        lockPosition: true,
        cellStyle: { overflow: 'visible' },
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        resizable: false
      }
    ],
    [user]
  )

  const defaultColDef = useMemo(
    () => ({
      ...agGrid.defaultColDef,
      flex: 1,
      minWidth: 150
    }),
    [agGrid.defaultColDef]
  )

  const onGridReady = (params: GridReadyEvent<Trader>) => {
    setGridApi(params.api)
  }

  const onFilterTextBoxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', e.target.value)
    }
  }

  const handleExport = () => {
    if (gridApi) {
      gridApi.exportDataAsCsv({
        fileName: `traders_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: ['name', 'createdAt']
      })
    }
  }

  if (isLoading) {
    return <div className="flex justify-center items-center h-64 text-muted-foreground">Loading...</div>
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">Error: {error.message}</div>
  }

  return (
    <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Traders</h1>
        {user.hasPermission(PERMISSION_ENTITY_TRADER, PERMISSION_ACTION_ADD) && (
          <Link
            to={`/${ROUTE_TRADER}/new`}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2"
          >
            <AddIcon />
            <span>Create Trader</span>
          </Link>
        )}
      </div>

      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search traders by name..."
          onChange={onFilterTextBoxChanged}
          className="flex-grow px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
          disabled={!gridApi || !traders || traders.length === 0}
        >
          <ExportIcon />
          <span>Export CSV</span>
        </button>
      </div>

      {tradersByMonth.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Cumulative Traders by Month</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-4">
            <ChartContainer config={traderChartConfig} className="h-[180px] w-full">
              <BarChart data={tradersByMonth} margin={{ left: 10, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                <YAxis hide allowDecimals={false} />
                <ChartTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(value) => [`${value}`, 'Traders']}
                  labelFormatter={(label) => label as string}
                />
                <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {!traders || traders.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No traders found matching your search criteria.</p>
      ) : (
        <div className="w-full min-h-[600px]">
          <AgGridReact
            rowData={traders}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            columnTypes={agGrid.columnTypes}
            dataTypeDefinitions={agGrid.dataTypeDefinitions}
            animateRows={true}
            rowSelection="multiple"
            pagination={true}
            paginationPageSize={20}
            theme={agGrid.theme}
            onGridReady={onGridReady}
            quickFilterText=""
          />
        </div>
      )}
    </div>
  )
}

export default Traders
